import { isNative } from "@stepapp/core";
import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from "react";
import { type StyleProp, StyleSheet, View, type ViewStyle } from "react-native";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";
import { useBreakpoints } from "../hooks/useBreakpoints";
import { palette, spacing } from "../styles";
import Block from "./Block";
import SvgIcon, { type IconNames } from "./SvgIcon";
import Touchable from "./Touchable";
import Typography from "./Typography";

type Props = {
  setIsLangDropdownOpen: Dispatch<SetStateAction<boolean>>;
  dropdownStyle?: StyleProp<ViewStyle>;
  onPress: (locale: string) => void;
  isVisible: boolean;
  currentLanguage: string;
};

const langDropdownItems = [
  { label: "Polski", locale: "pl" as IconNames },
  { label: "English", locale: "en" as IconNames },
  { label: "Svenska", locale: "sv" as IconNames },
  { label: "Norsk", locale: "no" as IconNames },
  { label: "Українська", locale: "uk" as IconNames },
];

const LanguageDropdown = ({
  setIsLangDropdownOpen,
  dropdownStyle,
  onPress,
  isVisible,
  currentLanguage,
}: Props) => {
  const { lg } = useBreakpoints();
  const langDropdownRef = React.useRef<View>(null);

  const currentLangItem = useMemo(
    () => langDropdownItems.find(({ locale }) => locale === currentLanguage),
    [currentLanguage],
  );

  useEffect(() => {
    if (isNative) return;

    document?.addEventListener("click", handleClick);
    return () => document?.removeEventListener("click", handleClick);
    function handleClick(e: ToDo) {
      // @ts-expect-error
      setIsLangDropdownOpen(langDropdownRef?.current?.contains?.(e.target));
    }
  }, [setIsLangDropdownOpen]);

  const toggle = useCallback(() => {
    setIsLangDropdownOpen((prev) => !prev);
  }, [setIsLangDropdownOpen]);

  return (
    <>
      <Touchable onPress={toggle} style={styles.langDropdownButton}>
        {currentLangItem && (
          <Block marginBottom={0.25}>
            <SvgIcon name={currentLangItem?.locale} size={24} />
          </Block>
        )}

        <Typography variant="paragraphBold" style={styles.langDropdownText}>
          {currentLangItem?.locale.toUpperCase()}
        </Typography>
      </Touchable>

      {isVisible && (
        <Animated.View
          entering={FadeIn.duration(100)}
          exiting={FadeOut.duration(50)}
          ref={langDropdownRef}
          style={[styles.langDropdown, { right: spacing.unit * (lg ? 30 : 18) }, dropdownStyle]}
        >
          {langDropdownItems.map(({ label, locale }) => (
            <Touchable key={label} onPress={() => onPress(locale)} style={styles.dropdownItem}>
              <SvgIcon name={locale} size={24} />
              <Typography
                numberOfLines={1}
                type="primary"
                variant="paragraph"
                style={styles.dropdownText}
              >
                {label}
              </Typography>
            </Touchable>
          ))}
        </Animated.View>
      )}
    </>
  );
};

export default React.memo(LanguageDropdown);

const styles = StyleSheet.create({
  dropdownText: {
    paddingLeft: spacing.unit * 2,
  },
  dropdownItem: {
    alignItems: "center",
    flexDirection: "row",
    height: spacing.unit * 3,
  },
  langDropdown: {
    gap: spacing.unit * 2,
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius,
    borderWidth: spacing.border,
    borderColor: palette.secondary.grey,
    boxShadow:
      "0px 41px 80px 0px rgba(0, 0, 0, 0.09), 0px 15.793px 25.481px 0px rgba(0, 0, 0, 0.05), 0px 3.341px 6.519px 0px rgba(0, 0, 0, 0.04)",
    position: "absolute",
    top: spacing.unit * 9,
    padding: spacing.unit * 3,
  },
  langDropdownButton: {
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: spacing.unit * 2,
    height: 41,
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius * 2,
    marginRight: spacing.unit,
  },
  langDropdownText: {
    marginLeft: spacing.unit,
  },
});
